// client/components/projekt/ProjektWartungUndZugaenge.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import api from "../../constants/api";
import { showSuccess, showError } from "../../utils/toast.js";
import { Search, X, RefreshCw } from "lucide-react";
import { DateOnlyField, todayYMD } from "../ui/DatePickers";
import { useAuth } from "../ui/AuthContext.jsx";
import useIsMobile from "../ui/useIsMobile.js";
import { useTheme } from "../../context/ThemeContext.jsx";
import { useConfirm } from "../ui/ConfirmationModal.jsx";
import { Z_INDEX } from "../ui/zIndex.js";
import AppButton from "../ui/AppButton.jsx";
import CollapsibleBlock from "../ui/CollapsibleBlock";
import { cn } from "../../utils/themeClasses.js";
import { successButtonLoadingClassName } from "../../design/tokens.js";
import { Input, Select } from "../forms/shared/FormAtoms.jsx";
function ActionLinks({
  items = [],
  align = "end"
}) {
  const alignClass = align === "start" ? "items-start" : "items-end";
  return <div className={`flex flex-col ${alignClass} gap-1 text-xs`}>
      {items.map((it, i) => <AppButton key={i} type="button" variant="ghost" onClick={it.onClick} className={cn("h-auto min-h-[44px] !p-0 !border-0 !shadow-none text-xs font-normal hover:underline", it.tone === "danger" ? "text-red-300 hover:underline" : "text-blue-300 hover:underline")}>
          {it.label}
        </AppButton>)}
    </div>;
}

/* ───────── WebAuthn (Passkey/FaceID) ───────── */
const WEBAUTHN_ROUTES = {
  options: "/webauthn/assert/start",
  // POST (Axios baseURL = "/api")
  verify: "/webauthn/assert/finish" // POST
};
async function requestPasskeyAssertion() {
  try {
    if (!("credentials" in navigator) || !navigator?.credentials?.get) {
      showError("Passkey wird auf diesem Gerät nicht unterstützt");
      return false;
    }
    const {
      data
    } = await api.post(WEBAUTHN_ROUTES.options);
    const opts = data?.options;
    if (!opts?.challenge) throw new Error("Keine WebAuthn-Optionen");
    const b64uToBuf = b64u => Uint8Array.from(atob(b64u.replace(/-/g, "+").replace(/_/g, "/")), c => c.charCodeAt(0)).buffer;
    const publicKey = {
      ...opts,
      challenge: b64uToBuf(opts.challenge),
      allowCredentials: (opts.allowCredentials || []).map(cred => ({
        ...cred,
        id: b64uToBuf(cred.id)
      }))
    };
    const cred = await navigator.credentials.get({
      publicKey
    });
    if (!cred) throw new Error("Keine Anmeldeinformationen");
    const bufToB64u = buf => btoa(String.fromCharCode(...new Uint8Array(buf))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
    const payload = {
      id: cred.id,
      rawId: bufToB64u(cred.rawId),
      type: cred.type,
      response: {
        clientDataJSON: bufToB64u(cred.response.clientDataJSON),
        authenticatorData: bufToB64u(cred.response.authenticatorData),
        signature: bufToB64u(cred.response.signature),
        userHandle: cred.response.userHandle ? bufToB64u(cred.response.userHandle) : null
      },
      clientExtensionResults: cred.getClientExtensionResults?.() || {},
      authenticatorAttachment: cred.authenticatorAttachment || undefined
    };
    const {
      data: verify
    } = await api.post(WEBAUTHN_ROUTES.verify, payload);
    return !!verify?.erfolg;
  } catch (e) {
    console.error(e);
    showError("Passkey/FaceID abgebrochen oder fehlgeschlagen");
    return false;
  }
}

/* ───────── Mini-Helpers ───────── */
const useDebounced = (val, ms = 200) => {
  const [q, setQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setQ((val || "").trim().toLowerCase()), ms);
    return () => clearTimeout(t);
  }, [val, ms]);
  return q;
};
const fmtDateCH = d => {
  if (!d) return "–";
  try {
    return new Date(d).toLocaleDateString("de-CH");
  } catch {
    return String(d);
  }
};
const capitalize = s => s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s;

/* ───────── Modal (Portal) ───────── */
function Modal({
  title,
  open,
  onClose,
  children,
  footer
}) {
  const panelRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const onKey = e => {
      if (e.key === "Escape") onClose?.();
    };
    const onDown = e => {
      if (panelRef.current && !panelRef.current.contains(e.target)) onClose?.();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onDown);
    };
  }, [open, onClose]);
    if (!open) return null;
  return createPortal(<div className="fixed inset-0" style={{
    zIndex: Z_INDEX.modal
  }}>
      <div className={`absolute inset-0 backdrop-blur-sm ${"bg-black/70"}`} />
      <div className="absolute inset-0 flex items-start md:items-center justify-center p-3 md:p-6">
        <div ref={panelRef} className={`w-full max-w-3xl rounded-2xl border shadow-2xl overflow-hidden transition-all ${"border-white/10 bg-[#0b1324]"}`} role="dialog" aria-modal="true" aria-label={title || "Dialog"}>
          <div className={`flex items-center gap-2 px-4 sm:px-6 py-3 border-b ${"border-white/10 bg-white/[0.03]"}`}>
            <div className={`text-lg font-semibold truncate ${"text-blue-100/90"}`}>
              {title}
            </div>
            <AppButton onClick={onClose} variant="ghost" className={cn("ml-auto !h-11 !w-11 !min-h-[44px] !min-w-[44px] !p-0 rounded-lg font-normal", "text-blue-100/80")} aria-label="Schliessen" type="button">
              <X className="w-5 h-5" />
            </AppButton>
          </div>
          <div className={`max-h-[70vh] overflow-y-auto px-4 sm:px-6 py-4 ${""}`}>
            {children}
          </div>
          {footer && <div className={`px-4 sm:px-6 py-3 border-t ${"border-white/10 bg-white/[0.02]"}`}>
              {footer}
            </div>}
        </div>
      </div>
    </div>, document.body);
}

/* ───────── Component ───────── */
const getInput = isDark => "bg-[#0b1324] text-blue-100 placeholder-blue-200/40 border border-white/10 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400/40";

// Modul-Konfiguration für bessere Anzeige
const MODUL_CONFIG = {
  sicherheit: {
    emoji: "🔐",
    label: "Sicherheit",
    color: "purple"
  },
  ladestation: {
    emoji: "🔌",
    label: "Ladestation",
    color: "green"
  },
  pv: {
    emoji: "☀️",
    label: "PV / Solar",
    color: "amber"
  }
};
const CONTRACTS_ENABLED = false;
export default function ProjektWartungUndZugaenge({
  kundeId,
  projektId = null,
  modul
}) {
    const confirm = useConfirm();
  const isMobile = useIsMobile();
  const { isDark } = useTheme();
  const {
    user: me
  } = useAuth();
  const isAdmin = Number(me?.rolle_id) >= 1 && Number(me?.rolle_id) <= 4;

  // Modul-spezifische Konfiguration
  const modulConfig = MODUL_CONFIG[modul] || {
    emoji: "🔑",
    label: capitalize(modul || "Allgemein"),
    color: "blue"
  };

  /* UI */
  const [search, setSearch] = useState("");
  const q = useDebounced(search, 200);
  const searchTrim = String(search || "").trim();
  const [showCredForm, setShowCredForm] = useState(false);
  const [showContractForm, setShowContractForm] = useState(false);
  const [editingCred, setEditingCred] = useState(null); // Zugang bearbeiten
  const [editingContract, setEditingContract] = useState(null); // Vertrag bearbeiten

  /* Data */
  const [users, setUsers] = useState([]);
  const [creds, setCreds] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  /* Reveal */
  const [reveal, setReveal] = useState({});
  const timersRef = useRef({});
  useEffect(() => {
    return () => {
      Object.values(timersRef.current || {}).forEach(t => {
        if (t) clearTimeout(t);
      });
      timersRef.current = {};
    };
  }, []);

  /* Assignees */
  const [assigneeModal, setAssigneeModal] = useState({
    open: false,
    contractId: null
  });
  const [assigneeTmp, setAssigneeTmp] = useState([]);
  const [assigneeSearch, setAssigneeSearch] = useState("");
  const baseParams = useMemo(() => ({
    params: {
      kunde_id: kundeId,
      projekt_id: projektId,
      modul
    }
  }), [kundeId, projektId, modul]);
  const userById = useMemo(() => Object.fromEntries(users.map(u => [u.id, u])), [users]);
  const initialCred = {
    bezeichnung: "",
    art: "password",
    benutzername: "",
    secret: "",
    hinweis: ""
  };
  const [credForm, setCredForm] = useState(initialCred);
  const initialContract = {
    typ: "",
    beginn: todayYMD(),
    laufzeit_monate: 12,
    notify_days_before: 14,
    auto_renew: false
  };
  const [contractForm, setContractForm] = useState(initialContract);

  /* Load */
  useEffect(() => {
    // Nur für privilegierte Benutzer (Rollen 1-4) laden
    if (!isAdmin) {
      setLoading(false);
      setUsers([]);
      setCreds([]);
      setContracts([]);
      return;
    }
    let ac = new AbortController();
    setLoading(true);
    (async () => {
      try {
        const [u, c1, c2] = await Promise.all([api.get("/service/users/options", {
          signal: ac.signal
        }), api.get("/service/credentials", {
          ...baseParams,
          signal: ac.signal
        }), CONTRACTS_ENABLED ? api.get("/service/contracts", {
          ...baseParams,
          signal: ac.signal
        }) : Promise.resolve({
          data: []
        })]);
        setUsers(Array.isArray(u.data) ? u.data : []);
        setCreds(Array.isArray(c1.data) ? c1.data : []);
        setContracts(Array.isArray(c2.data) ? c2.data : []);
      } catch (e) {
        if (e?.name !== "CanceledError") {
          console.error(e);
          showError("Laden fehlgeschlagen");
          setUsers([]);
          setCreds([]);
          setContracts([]);
        }
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kundeId, projektId, modul, isAdmin]);

  /* Filters */
  const visibleCreds = useMemo(() => {
    if (!q) return creds;
    return creds.filter(c => [c.bezeichnung, c.art, c.benutzername, c.hinweis].filter(Boolean).some(s => String(s).toLowerCase().includes(q)));
  }, [creds, q]);
  const visibleContracts = useMemo(() => {
    if (!q) return contracts;
    return contracts.filter(v => [v.typ, v.modul, v.status].filter(Boolean).some(s => String(s).toLowerCase().includes(q)));
  }, [contracts, q]);

  /* Header/Stats (wie andere Projekt-Blöcke: kompakt rechts, Details im Tooltip) */
  const headerInfo = useMemo(
    () => (CONTRACTS_ENABLED ? `${creds.length} · ${contracts.length} Vtr.` : `${creds.length} Zugänge`),
    [creds.length, contracts.length]
  );
  const headerInfoMobile = useMemo(() => String(creds.length), [creds.length]);
  const headerInfoTitle = useMemo(
    () =>
      CONTRACTS_ENABLED
        ? `${modulConfig.label}: ${creds.length} Zugänge · ${contracts.length} Verträge`
        : `${modulConfig.label}: ${creds.length} Zugänge`,
    [modulConfig.label, creds.length, contracts.length]
  );

  // „Bald fällig“: nächste_erneuerung innerhalb notify_days_before ab heute
  const stats = useMemo(() => {
    if (!CONTRACTS_ENABLED) return {
      dueSoon: 0
    };
    const today = new Date();
    const dueSoon = contracts.filter(v => {
      const nd = v?.naechste_erneuerung ? new Date(v.naechste_erneuerung) : null;
      const days = Number(v?.notify_days_before) || 0;
      if (!nd) return false;
      const diff = Math.ceil((nd - today) / (1000 * 60 * 60 * 24));
      return diff >= 0 && diff <= days;
    }).length;
    return {
      dueSoon
    };
  }, [contracts]);

  /* Actions – Credentials */
  const saveCred = async e => {
    e?.preventDefault?.();
    if (!credForm.bezeichnung.trim() || !String(credForm.secret || "").trim()) {
      showError("Bezeichnung und Passwort/Code sind Pflichtfelder.");
      return;
    }
    try {
      setSaving(true);
      await api.post("/service/credentials", {
        kunde_id: kundeId,
        projekt_id: projektId,
        modul,
        bezeichnung: credForm.bezeichnung.trim(),
        art: (credForm.art || "password").trim(),
        benutzername: credForm.benutzername?.trim() || null,
        secret: String(credForm.secret || ""),
        hinweis: credForm.hinweis?.trim() || null
      });
      const res = await api.get("/service/credentials", baseParams);
      setCreds(Array.isArray(res.data) ? res.data : []);
      showSuccess("Zugang gespeichert");
      setShowCredForm(false);
      setCredForm(initialCred);
    } catch (e) {
      console.error(e);
      showError("Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };
  const secureReveal = async id => {
    const ok = await requestPasskeyAssertion();
    if (!ok) return;
    try {
      const {
        data
      } = await api.get(`/service/credentials/${id}/reveal`);
      setReveal(p => ({
        ...p,
        [id]: data?.secret || ""
      }));
      if (timersRef.current[id]) clearTimeout(timersRef.current[id]);
      timersRef.current[id] = setTimeout(() => {
        setReveal(p => ({
          ...p,
          [id]: null
        }));
        delete timersRef.current[id];
      }, 20000);
    } catch {
      showError("Reveal fehlgeschlagen");
    }
  };
  const toggleShowSecret = async id => {
    if (reveal[id]) {
      setReveal(p => ({
        ...p,
        [id]: null
      }));
      if (timersRef.current[id]) clearTimeout(timersRef.current[id]);
      return;
    }
    await secureReveal(id);
  };
  const copySecret = async id => {
    const s = reveal[id];
    if (!s) return;
    try {
      await navigator.clipboard.writeText(String(s));
      showSuccess("Secret kopiert");
    } catch {
      showError("Kopieren nicht möglich");
    }
  };
  const delCred = async id => {
    const {
      ok
    } = await confirm({
      title: "Zugang löschen",
      message: "Zugang löschen?",
      variant: "danger",
      confirmLabel: "Löschen"
    });
    if (!ok) return;
    try {
      await api.delete(`/service/credentials/${id}`);
      setCreds(prev => prev.filter(x => x.id !== id));
      showSuccess("In Papierkorb verschoben");
    } catch {
      showError("Löschen fehlgeschlagen");
    }
  };

  // Zugang bearbeiten
  const openEditCred = cred => {
    setEditingCred({
      id: cred.id,
      bezeichnung: cred.bezeichnung || "",
      art: cred.art || "password",
      benutzername: cred.benutzername || "",
      secret: "",
      // Secret muss neu eingegeben werden (Sicherheit)
      hinweis: cred.hinweis || ""
    });
  };
  const updateCred = async e => {
    e?.preventDefault?.();
    if (!editingCred) return;
    if (!editingCred.bezeichnung.trim()) {
      showError("Bezeichnung ist Pflichtfeld.");
      return;
    }
    try {
      setSaving(true);
      const payload = {
        bezeichnung: editingCred.bezeichnung.trim(),
        art: (editingCred.art || "password").trim(),
        benutzername: editingCred.benutzername?.trim() || null,
        hinweis: editingCred.hinweis?.trim() || null
      };
      // Secret nur updaten wenn eingegeben
      if (editingCred.secret?.trim()) {
        payload.secret = editingCred.secret.trim();
      }
      await api.patch(`/service/credentials/${editingCred.id}`, payload);
      const res = await api.get("/service/credentials", baseParams);
      setCreds(Array.isArray(res.data) ? res.data : []);
      showSuccess("Zugang aktualisiert");
      setEditingCred(null);
    } catch (e) {
      console.error(e);
      showError("Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  /* Actions – Contracts */
  const saveContract = async e => {
    e?.preventDefault?.();
    if (!contractForm.typ.trim() || !String(contractForm.beginn || "").trim()) {
      showError("Typ und Beginn sind Pflichtfelder.");
      return;
    }
    try {
      setSaving(true);
      await api.post("/service/contracts", {
        kunde_id: kundeId,
        projekt_id: projektId,
        modul,
        typ: contractForm.typ.trim(),
        beginn: String(contractForm.beginn).trim(),
        laufzeit_monate: Number(contractForm.laufzeit_monate) || 12,
        notify_days_before: Number(contractForm.notify_days_before) || 14,
        auto_renew: !!contractForm.auto_renew,
        assigned_user_ids: assigneeTmp.map(Number).filter(n => n > 0)
      });
      const res = await api.get("/service/contracts", baseParams);
      setContracts(Array.isArray(res.data) ? res.data : []);
      showSuccess("Vertrag gespeichert");
      setShowContractForm(false);
      setContractForm(initialContract);
      setAssigneeTmp([]);
    } catch {
      showError("Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };
  const delContract = async id => {
    const {
      ok
    } = await confirm({
      title: "Vertrag löschen",
      message: "Vertrag löschen?",
      variant: "danger",
      confirmLabel: "Löschen"
    });
    if (!ok) return;
    try {
      await api.delete(`/service/contracts/${id}`);
      setContracts(prev => prev.filter(x => x.id !== id));
      showSuccess("In Papierkorb verschoben");
    } catch {
      showError("Löschen fehlgeschlagen");
    }
  };
  const renewContract = async id => {
    try {
      await api.post(`/service/contracts/${id}/renew`);
      const res = await api.get("/service/contracts", baseParams);
      setContracts(Array.isArray(res.data) ? res.data : []);
      showSuccess("Vertrag verlängert");
    } catch (e) {
      console.error(e);
      showError("Verlängern fehlgeschlagen");
    }
  };

  // Vertrag bearbeiten öffnen
  const openEditContract = contract => {
    const current = Array.isArray(contract.assigned_user_ids) ? contract.assigned_user_ids : [];
    setAssigneeTmp(current);
    setAssigneeSearch("");
    setEditingContract({
      id: contract.id,
      typ: contract.typ || "",
      beginn: contract.beginn ? String(contract.beginn).slice(0, 10) : todayYMD(),
      laufzeit_monate: contract.laufzeit_monate || 12,
      notify_days_before: contract.notify_days_before ?? 14,
      auto_renew: !!contract.auto_renew
    });
  };
  const updateContract = async e => {
    e?.preventDefault?.();
    if (!editingContract) return;
    if (!editingContract.typ.trim() || !editingContract.beginn) {
      showError("Typ und Beginn sind Pflichtfelder.");
      return;
    }
    try {
      setSaving(true);
      await api.patch(`/service/contracts/${editingContract.id}`, {
        typ: editingContract.typ.trim(),
        beginn: String(editingContract.beginn).trim(),
        laufzeit_monate: Number(editingContract.laufzeit_monate) || 12,
        notify_days_before: Number(editingContract.notify_days_before) || 14,
        auto_renew: !!editingContract.auto_renew,
        assigned_user_ids: assigneeTmp.map(Number).filter(n => n > 0)
      });
      const res = await api.get("/service/contracts", baseParams);
      setContracts(Array.isArray(res.data) ? res.data : []);
      showSuccess("Vertrag aktualisiert");
      setEditingContract(null);
      setAssigneeTmp([]);
    } catch (e) {
      console.error(e);
      showError("Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };
  const openAssignees = contract => {
    const current = Array.isArray(contract.assigned_user_ids) ? contract.assigned_user_ids : [];
    setAssigneeTmp(current);
    setAssigneeSearch("");
    setAssigneeModal({
      open: true,
      contractId: contract.id
    });
  };
  const toggleAssignee = uid => setAssigneeTmp(prev => prev.includes(uid) ? prev.filter(x => x !== uid) : [...prev, uid]);
  const saveAssignees = async () => {
    const ids = assigneeTmp.map(Number).filter(n => Number.isFinite(n) && n > 0);
    try {
      setSaving(true);
      await api.post(`/service/contracts/${assigneeModal.contractId}/assign`, {
        user_ids: ids
      });
      const res = await api.get("/service/contracts", baseParams);
      setContracts(Array.isArray(res.data) ? res.data : []);
      showSuccess("Empfänger aktualisiert");
      setAssigneeModal({
        open: false,
        contractId: null
      });
    } catch {
      showError("Aktualisieren fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };
  const filteredUsers = useMemo(() => {
    const s = assigneeSearch.trim().toLowerCase();
    if (!s) return users;
    return users.filter(u => (u.name || "").toLowerCase().includes(s));
  }, [assigneeSearch, users]);

  /* Render */
  return (
    <>
    <CollapsibleBlock
      title={`${modulConfig.emoji} Zugänge & Wartung`}
      headerInfo={headerInfo}
      headerInfoMobile={headerInfoMobile}
      headerInfoTitle={headerInfoTitle}
      panelId={`wartung-zug-${modul}-${projektId ?? "k"}-${kundeId}`}
      defaultOpen={false}
      className="overflow-hidden"
      contentClassName="px-3 sm:px-4 pb-3 sm:pb-4"
      useChevronUp={true}
      showBorderBottom={true}
    >
      <div className="min-w-0 text-primary">
        <div className="mt-1.5 mb-1.5 flex flex-wrap items-center gap-2">
          <div className="relative w-full min-w-0 flex-1 sm:w-auto sm:min-w-[10rem]">
            <Input
              type="text"
              inputMode="search"
              autoComplete="off"
              enterKeyHint="search"
              placeholder="Suche…"
              title="Bezeichnung, Benutzername, Hinweis oder Vertragstyp …"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`px-3 py-2 min-h-[40px] w-full text-sm [appearance:textfield] [&::-webkit-search-cancel-button]:hidden ${searchTrim ? "pr-10" : "pr-3"}`}
              style={isMobile ? { fontSize: "16px" } : undefined}
              aria-label="Zugänge durchsuchen"
              role="searchbox"
            />
            {searchTrim ? (
              <AppButton
                type="button"
                variant="ghost"
                onClick={() => setSearch("")}
                className="absolute right-1 top-1/2 -translate-y-1/2 !min-h-[36px] !min-w-[36px] !p-0 !border-0 !bg-transparent !shadow-none text-blue-200/70 hover:!bg-transparent hover:text-white"
                aria-label="Suche löschen"
                title="Suche löschen"
              >
                ✕
              </AppButton>
            ) : null}
          </div>

          <AppButton
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => {
              setShowCredForm((v) => !v);
              if (!showCredForm) setShowContractForm(false);
            }}
            className="shrink-0 min-h-9"
            title={showCredForm ? "Erfassung schließen" : "Neuen Zugang erfassen"}
          >
            {showCredForm ? "Abbrechen" : "Zugang erfassen"}
          </AppButton>

          {CONTRACTS_ENABLED ? (
            <AppButton
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => {
                setShowContractForm((v) => !v);
                if (!showContractForm) setShowCredForm(false);
              }}
              className="shrink-0 min-h-9"
              title={showContractForm ? "Erfassung schließen" : "Jahresvertrag erfassen"}
            >
              {showContractForm ? "Abbrechen" : "Jahresvertrag erfassen"}
            </AppButton>
          ) : null}
        </div>

          {/* Loading */}
          {loading ? <div className="space-y-2 mt-3 animate-pulse">
              <div className={`h-16 rounded ${"bg-white/10"}`} />
              <div className={`h-16 rounded ${"bg-white/10"}`} />
              <div className={`h-16 rounded ${"bg-white/10"}`} />
            </div> : <>
              {/* Formular: Zugang */}
              {showCredForm && <form onSubmit={saveCred} className="relative border border-dashed border-default rounded-lg p-4 mb-4 bg-surface text-primary">
    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
      {/* Bezeichnung */}
      <label className="flex flex-col text-sm gap-1">
        <span className={"opacity-80"}>Bezeichnung *</span>
        <Input className={getInput(isDark)} value={credForm.bezeichnung} onChange={e => setCredForm(s => ({
                ...s,
                bezeichnung: e.target.value
              }))} autoComplete="off" spellCheck={false} autoCapitalize="off" inputMode="text" required />
      </label>

      {/* Art */}
      <label className="flex flex-col text-sm gap-1">
        <span className={"opacity-80"}>Art</span>
        <Select className={getInput(isDark)} value={credForm.art} onChange={e => setCredForm(s => ({
                ...s,
                art: e.target.value
              }))}>
          <option value="password">Password</option>
          <option value="pin">Pin</option>
          <option value="code">Code</option>
          <option value="portal">Portal</option>
        </Select>
      </label>

      {/* Benutzername */}
      <label className="flex flex-col text-sm gap-1 md:col-span-2">
        <span className={"opacity-80"}>Benutzername</span>
        <Input className={getInput(isDark)} value={credForm.benutzername} onChange={e => setCredForm(s => ({
                ...s,
                benutzername: e.target.value
              }))} placeholder="optional" autoComplete="username" spellCheck={false} autoCapitalize="off" inputMode="text" />
      </label>

      {/* Secret */}
      <label className="flex flex-col text-sm gap-1 md:col-span-4">
        <span className={"opacity-80"}>Passwort / Code *</span>
        <Input className={`${getInput(isDark)} font-mono`} value={credForm.secret} onChange={e => setCredForm(s => ({
                ...s,
                secret: e.target.value
              }))} autoComplete="new-password" spellCheck={false} autoCapitalize="off" inputMode="text" required />
      </label>

      {/* Hinweis */}
      <label className="flex flex-col text-sm gap-1 md:col-span-4">
        <span className="opacity-80">Hinweis</span>
        <Input className={getInput(isDark)} value={credForm.hinweis} onChange={e => setCredForm(s => ({
                ...s,
                hinweis: e.target.value
              }))} placeholder="optional" autoComplete="off" spellCheck={false} autoCapitalize="sentences" />
      </label>
    </div>

    {/* Desktop-Action */}
    <div className="hidden md:block mt-4 text-right">
      <AppButton type="submit" variant="success" disabled={saving} className={cn("px-4 py-2 text-sm font-normal", saving && successButtonLoadingClassName)}>
        Speichern
      </AppButton>
    </div>

    {/* Mobile Sticky-Action (mit Safe-Area) */}
    <div className="md:hidden sticky bottom-0 inset-x-0 z-10 -mx-4 -mb-4 px-4 pb-4" style={{
            paddingBottom: "max(1rem, env(safe-area-inset-bottom))"
          }}>
      <div className={`backdrop-blur rounded-2xl p-3 shadow-xl border ${"bg-[#0b1324]/90 border-white/10"}`}>
        <AppButton type="submit" variant="success" disabled={saving} className={cn("w-full px-4 py-3 text-sm font-normal", saving && successButtonLoadingClassName)}>
          Speichern
        </AppButton>
      </div>
    </div>
  </form>}

              {/* Formular: Vertrag */}
              {CONTRACTS_ENABLED && showContractForm && <form onSubmit={saveContract} className="relative border border-dashed border-default rounded-lg p-4 mb-4 bg-surface text-primary">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <label className="flex flex-col text-sm gap-1 md:col-span-2">
                      <span className={"opacity-80"}>Vertragstyp *</span>
                      <Input className={getInput(isDark)} value={contractForm.typ} onChange={e => setContractForm(s => ({
                ...s,
                typ: e.target.value
              }))} placeholder='z. B. "Alarmanlagen-Wartung"' required />
                    </label>
                    <div className="flex flex-col text-sm gap-1">
                      <DateOnlyField label="Beginn *" value={contractForm.beginn} onChange={v => setContractForm(s => ({
                ...s,
                beginn: v
              }))} />
                    </div>
                    <label className="flex flex-col text-sm gap-1">
                      <span className={"opacity-80"}>Laufzeit (Monate)</span>
                      <Input type="number" min={1} step="1" inputMode="numeric" className={getInput(isDark)} value={contractForm.laufzeit_monate} onChange={e => setContractForm(s => ({
                ...s,
                laufzeit_monate: Number(e.target.value)
              }))} />
                    </label>
                    <label className="flex flex-col text-sm gap-1">
                      <span className={"opacity-80"}>Reminder (Tage vorher)</span>
                      <Input type="number" min={0} step="1" inputMode="numeric" className={getInput(isDark)} value={contractForm.notify_days_before} onChange={e => setContractForm(s => ({
                ...s,
                notify_days_before: Number(e.target.value)
              }))} />
                    </label>
                    <label className="flex items-center gap-2 text-sm md:col-span-1 mt-1">
                      <input type="checkbox" className="w-5 h-5 accent-blue-500" checked={!!contractForm.auto_renew} onChange={e => setContractForm(s => ({
                ...s,
                auto_renew: e.target.checked
              }))} />
                      <span>Auto-Renew</span>
                    </label>
                  </div>

                  {/* Empfänger im Formular */}
                  <AssigneePicker isDark={true} users={users} assigneeSearch={assigneeSearch} setAssigneeSearch={setAssigneeSearch} filteredUsers={filteredUsers} assigneeTmp={assigneeTmp} toggleAssignee={toggleAssignee} />

                  <div className="hidden md:block mt-4 text-right">
                    <AppButton type="submit" variant="success" disabled={saving} className={cn("px-4 py-2 text-sm font-normal", saving && successButtonLoadingClassName)}>
                      Speichern
                    </AppButton>
                  </div>
                  <div className="md:hidden sticky bottom-0 inset-x-0 z-10 -mx-4 -mb-4 px-4 pb-4" style={{
            paddingBottom: "max(1rem, env(safe-area-inset-bottom))"
          }}>
                    <div className={`backdrop-blur rounded-2xl p-3 shadow-xl border ${"bg-[#0b1324]/90 border-white/10"}`}>
                      <AppButton type="submit" variant="success" disabled={saving} className={cn("w-full px-4 py-3 text-sm font-normal", saving && successButtonLoadingClassName)}>
                        Speichern
                      </AppButton>
                    </div>
                  </div>
                </form>}

              {/* Liste: Zugänge */}
              <div className={`rounded-2xl border p-3 md:p-4 ${"border-white/10 bg-white/[0.03]"}`}>
                <div className={`font-semibold mb-2 ${"text-blue-100/90"}`}>Zugänge</div>

                {/* Desktop: Tabelle */}
                <div className="hidden md:block">
                  <div className={`max-h-[50vh] md:max-h-[360px] overflow-y-auto overflow-x-hidden border rounded ${"border-white/10"}`}>
                    <table className={`w-full text-sm border-collapse table-fixed ${"text-blue-100"}`}>
                      <colgroup>
                        <col style={{
                    width: "28%"
                  }} />
                        <col style={{
                    width: "22%"
                  }} />
                        <col style={{
                    width: "26%"
                  }} />
                        <col style={{
                    width: "16%"
                  }} />
                        <col style={{
                    width: "8%"
                  }} />
                      </colgroup>
                      <thead className={`sticky top-0 border-b text-left ${"bg-white/5 border-white/10"}`}>
                        <tr>
                          <th className="px-3 py-2">Bezeichnung</th>
                          <th className="px-3 py-2">Benutzername</th>
                          <th className="px-3 py-2">Hinweis</th>
                          <th className="px-3 py-2">Secret</th>
                          <th className="px-3 py-2 text-right">Aktion</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleCreds.length === 0 ? <tr>
                            <td colSpan={5} className="px-3 py-4 text-blue-200/70">
                              Noch keine Zugänge erfasst.
                            </td>
                          </tr> : visibleCreds.map(c => {
                    const canReveal = ["password", "pin"].includes(String(c.art || "").toLowerCase());
                    return <tr key={c.id} className={`border-t ${"border-white/10"}`}>
                                <td className="px-3 py-2 align-top">
                                  <div className="font-medium break-words">
                                    {c.bezeichnung}{" "}
                                    <span className="text-xs opacity-70">
                                      ({capitalize(c.art)})
                                    </span>
                                  </div>
                                </td>
                                <td className="px-3 py-2 align-top break-words">
                                  {c.benutzername || "—"}
                                </td>
                                <td className="px-3 py-2 align-top break-words">
                                  {c.hinweis || "—"}
                                </td>
                                <td className="px-3 py-2 align-top">
                                  {reveal[c.id] ? <span className="font-mono break-all">
                                      {reveal[c.id]}
                                    </span> : canReveal ? <span aria-hidden>•••••••</span> : "—"}
                                </td>
                                <td className="px-3 py-2 align-top text-right">
                                <ActionLinks items={[...(canReveal ? [{
                          label: reveal[c.id] ? "Verbergen" : "Anzeigen",
                          onClick: () => toggleShowSecret(c.id)
                        }] : []), ...(reveal[c.id] ? [{
                          label: "Kopieren",
                          onClick: () => copySecret(c.id)
                        }] : []), ...(isAdmin ? [{
                          label: "Bearbeiten",
                          onClick: () => openEditCred(c)
                        }] : []), ...(isAdmin ? [{
                          label: "Löschen",
                          onClick: () => delCred(c.id),
                          tone: "danger"
                        }] : [])]} />
                              </td>
                              </tr>;
                  })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Mobile: Cards */}
                <div className="md:hidden space-y-2">
                  {visibleCreds.length === 0 ? <div className="opacity-60 text-sm">
                      Noch keine Zugänge erfasst.
                    </div> : visibleCreds.map(c => {
              const canReveal = ["password", "pin"].includes(String(c.art || "").toLowerCase());
              return <div key={c.id} className={`rounded border p-3 ${"border-white/10 bg-[#0b1324]/50"}`}>
                          <div className="font-medium">
                            {c.bezeichnung}{" "}
                            <span className="text-xs opacity-70">
                              ({capitalize(c.art)})
                            </span>
                          </div>
                          {c.benutzername && <div className="text-sm opacity-80 break-all">
                              User: {c.benutzername}
                            </div>}
                          {c.hinweis && <div className="text-xs opacity-60 mt-1">
                              {c.hinweis}
                            </div>}
                          {reveal[c.id] && <div className="mt-2 font-mono text-sm break-all">
                              Secret: {reveal[c.id]}
                            </div>}
                         <div className="mt-2">
                          <ActionLinks align="start" items={[...(canReveal ? [{
                    label: reveal[c.id] ? "Verbergen" : "Anzeigen",
                    onClick: () => toggleShowSecret(c.id)
                  }] : []), ...(reveal[c.id] ? [{
                    label: "Kopieren",
                    onClick: () => copySecret(c.id)
                  }] : []), ...(isAdmin ? [{
                    label: "Bearbeiten",
                    onClick: () => openEditCred(c)
                  }] : []), ...(isAdmin ? [{
                    label: "Löschen",
                    onClick: () => delCred(c.id),
                    tone: "danger"
                  }] : [])]} />
                        </div>
                        </div>;
            })}
                </div>
              </div>

              {/* Liste: Verträge */}
              {CONTRACTS_ENABLED && <div className={`mt-4 rounded-2xl border p-3 md:p-4 ${"border-white/10 bg-white/[0.03]"}`}>
                <div className={`font-semibold mb-2 ${"text-blue-100/90"}`}>
                  Jahresverträge
                </div>

                {/* Desktop Tabelle: feste Spalten + kein X-Scroll */}
                <div className="hidden md:block">
                  <div className={`max-h-[55vh] md:max-h-[432px] overflow-y-auto overflow-x-hidden border rounded ${"border-white/10"}`}>
                    <table className={`w-full text-sm border-collapse table-fixed ${"text-blue-100"}`}>
                      <colgroup>
                        <col style={{
                    width: "24%"
                  }} />
                        <col style={{
                    width: "14%"
                  }} />
                        <col style={{
                    width: "16%"
                  }} />
                        <col style={{
                    width: "12%"
                  }} />
                        <col style={{
                    width: "34%"
                  }} />
                        <col style={{
                    width: "12%"
                  }} />
                      </colgroup>
                      <thead className={`sticky top-0 border-b text-left ${"bg-white/5 border-white/10"}`}>
                        <tr>
                          <th className="px-3 py-2">Typ</th>
                          <th className="px-3 py-2">Beginn</th>
                          <th className="px-3 py-2">Nächste Erneuerung</th>
                          <th className="px-3 py-2">Status</th>
                          <th className="px-3 py-2">Empfänger</th>
                          <th className="px-3 py-2 text-right">Aktion</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleContracts.length === 0 ? <tr>
                            <td colSpan={6} className={`px-3 py-4 ${"text-blue-200/70"}`}>
                              Noch keine Verträge erfasst.
                            </td>
                          </tr> : visibleContracts.map(v => {
                    const names = (v.assigned_user_ids || []).map(id => userById[id]?.name || null).filter(Boolean).join(", ") || "—";
                    return <tr key={v.id} className={`border-t align-top ${"border-white/10"}`}>
                                <td className="px-3 py-2">
                                  <div className="font-medium flex items-center gap-2">
                                    <span className="break-words">{v.typ}</span>
                                    {!!v.auto_renew && <span title="Auto-Renew aktiv" className="inline-flex items-center gap-1 text-green-300 text-[11px]">
                                        <RefreshCw className="w-3 h-3" />
                                        Auto-Renew
                                      </span>}
                                  </div>
                                </td>
                                <td className="px-3 py-2">{fmtDateCH(v.beginn)}</td>
                                <td className="px-3 py-2">
                                  {fmtDateCH(v.naechste_erneuerung)}
                                </td>
                                <td className="px-3 py-2">{v.status || "—"}</td>
                                <td className="px-3 py-2">
                                  <div className={`whitespace-normal break-words ${"text-blue-100/90"}`} style={{
                          wordBreak: "break-word"
                        }} title={names}>
                                    {names}
                                  </div>
                                </td>
                                <td className="px-3 py-2 text-right">
                                  <ActionLinks items={[{
                          label: "Bearbeiten",
                          onClick: () => openEditContract(v)
                        }, ...(isAdmin ? [{
                          label: "Löschen",
                          onClick: () => delContract(v.id),
                          tone: "danger"
                        }] : [])]} />
                                </td>
                              </tr>;
                  })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Mobile Cards */}
                <div className="md:hidden space-y-2">
                  {visibleContracts.length === 0 ? <div className={`text-sm ${"opacity-60"}`}>
                      Noch keine Verträge erfasst.
                    </div> : visibleContracts.map(v => {
              const names = (v.assigned_user_ids || []).map(id => userById[id]?.name || null).filter(Boolean).join(", ") || "—";
              return <div key={v.id} className={`rounded border p-3 ${"border-white/10 bg-[#0b1324]/50"}`}>
                          <div className="font-medium flex items-center gap-2">
                            <span>{v.typ}</span>
                            {!!v.auto_renew && <span className="inline-flex items-center gap-1 text-green-300 text-xs">
                                <RefreshCw className="w-3 h-3" />
                                Auto-Renew
                              </span>}
                          </div>
                          <div className="text-sm opacity-80">
                            Beginn {fmtDateCH(v.beginn)} · nächste Erneuerung{" "}
                            {fmtDateCH(v.naechste_erneuerung)}
                            {v.status && <> · Status {v.status}</>}
                          </div>
                          <div className="text-xs opacity-70 mt-1">
                            Empfänger: {names}
                          </div>
                          <div className="mt-2">
                          <ActionLinks align="start" items={[{
                    label: "Bearbeiten",
                    onClick: () => openEditContract(v)
                  }, ...(isAdmin ? [{
                    label: "Löschen",
                    onClick: () => delContract(v.id),
                    tone: "danger"
                  }] : [])]} />
                        </div>
                        </div>;
            })}
                </div>
              </div>}

              {/* Footer-Stats: nur Bald fällig + Gesamt */}
              <div className={`mt-4 text-sm pt-3 grid grid-cols-1 md:grid-cols-2 gap-3 border-t ${"text-blue-100 border-white/10"}`}>
                {CONTRACTS_ENABLED && <p>⏰ <strong>Bald fällig:</strong> {stats.dueSoon}</p>}
                <p>
                  🧾 <strong>Gesamt:</strong> {visibleCreds.length} Zugänge{CONTRACTS_ENABLED ? ` · ${visibleContracts.length} Verträge` : ""}
                </p>
              </div>
            </>}
      </div>
    </CollapsibleBlock>

      {/* Zugang bearbeiten Modal */}
      {CONTRACTS_ENABLED && <Modal title="Zugang bearbeiten" open={!!editingCred} onClose={() => setEditingCred(null)} footer={<div className="flex justify-end gap-2">
            <AppButton type="button" variant="secondary" className={cn("px-3 py-1.5 text-sm min-h-[44px] font-normal", "")} onClick={() => setEditingCred(null)}>
              Abbrechen
            </AppButton>
            <AppButton type="button" variant="success" onClick={updateCred} disabled={saving} className={cn("px-3 py-1.5 text-sm min-h-[44px] font-normal inline-flex items-center gap-2", saving && successButtonLoadingClassName)}>
              Speichern
            </AppButton>
          </div>}>
        {editingCred && <form onSubmit={updateCred} className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="flex flex-col text-sm gap-1">
              <span className={"opacity-80"}>Bezeichnung *</span>
              <Input className={getInput(isDark)} value={editingCred.bezeichnung} onChange={e => setEditingCred(s => ({
            ...s,
            bezeichnung: e.target.value
          }))} required />
            </label>
            <label className="flex flex-col text-sm gap-1">
              <span className={"opacity-80"}>Art</span>
              <Select className={getInput(isDark)} value={editingCred.art} onChange={e => setEditingCred(s => ({
            ...s,
            art: e.target.value
          }))}>
                <option value="password">Password</option>
                <option value="pin">Pin</option>
                <option value="code">Code</option>
                <option value="portal">Portal</option>
              </Select>
            </label>
            <label className="flex flex-col text-sm gap-1 md:col-span-2">
              <span className={"opacity-80"}>Benutzername</span>
              <Input className={getInput(isDark)} value={editingCred.benutzername} onChange={e => setEditingCred(s => ({
            ...s,
            benutzername: e.target.value
          }))} placeholder="optional" />
            </label>
            <label className="flex flex-col text-sm gap-1 md:col-span-2">
              <span className={"opacity-80"}>Neues Passwort / Code</span>
              <Input className={`${getInput(isDark)} font-mono`} value={editingCred.secret} onChange={e => setEditingCred(s => ({
            ...s,
            secret: e.target.value
          }))} placeholder="Leer lassen um bestehenden Wert zu behalten" />
              <span className={`text-xs ${"text-blue-200/60"}`}>
                Aus Sicherheitsgründen wird das aktuelle Secret nicht angezeigt
              </span>
            </label>
            <label className="flex flex-col text-sm gap-1 md:col-span-2">
              <span className={"opacity-80"}>Hinweis</span>
              <Input className={getInput(isDark)} value={editingCred.hinweis} onChange={e => setEditingCred(s => ({
            ...s,
            hinweis: e.target.value
          }))} placeholder="optional" />
            </label>
          </form>}
      </Modal>}

      {/* Vertrag bearbeiten Modal */}
      {CONTRACTS_ENABLED && <Modal title="Vertrag bearbeiten" open={!!editingContract} onClose={() => {
      setEditingContract(null);
      setAssigneeTmp([]);
    }} footer={<div className="flex flex-wrap justify-between gap-2">
            <AppButton type="button" variant="ghost" onClick={async () => {
        if (!editingContract) return;
        const {
          ok
        } = await confirm({
          title: "Vertrag verlängern",
          message: "Vertrag um einen weiteren Zeitraum verlängern?",
          variant: "default",
          confirmLabel: "Verlängern"
        });
        if (ok) {
          await renewContract(editingContract.id);
          setEditingContract(null);
          setAssigneeTmp([]);
        }
      }} className={cn("px-3 py-1.5 text-sm min-h-[44px] font-normal inline-flex items-center gap-2", "!bg-blue-600/30 hover:!bg-blue-600/40 !border-blue-400/30 !text-blue-200")}>
              <RefreshCw className="w-4 h-4" /> Verlängern
            </AppButton>
            <div className="flex gap-2">
              <AppButton type="button" variant="secondary" className={cn("px-3 py-1.5 text-sm min-h-[44px] font-normal", "")} onClick={() => {
          setEditingContract(null);
          setAssigneeTmp([]);
        }}>
                Abbrechen
              </AppButton>
              <AppButton type="button" variant="success" onClick={updateContract} disabled={saving} className={cn("px-3 py-1.5 text-sm min-h-[44px] font-normal inline-flex items-center gap-2", saving && successButtonLoadingClassName)}>
                Speichern
              </AppButton>
            </div>
          </div>}>
        {editingContract && <form onSubmit={updateContract} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="flex flex-col text-sm gap-1 md:col-span-2">
                <span className={"opacity-80"}>Vertragstyp *</span>
                <Input className={getInput(isDark)} value={editingContract.typ} onChange={e => setEditingContract(s => ({
              ...s,
              typ: e.target.value
            }))} required />
              </label>
              <div className="flex flex-col text-sm gap-1">
                <DateOnlyField label="Beginn *" value={editingContract.beginn} onChange={v => setEditingContract(s => ({
              ...s,
              beginn: v
            }))} />
              </div>
              <label className="flex flex-col text-sm gap-1">
                <span className={"opacity-80"}>Laufzeit (Monate)</span>
                <Input type="number" min={1} step="1" inputMode="numeric" className={getInput(isDark)} value={editingContract.laufzeit_monate} onChange={e => setEditingContract(s => ({
              ...s,
              laufzeit_monate: Number(e.target.value)
            }))} />
              </label>
              <label className="flex flex-col text-sm gap-1">
                <span className={"opacity-80"}>Reminder (Tage vorher)</span>
                <Input type="number" min={0} step="1" inputMode="numeric" className={getInput(isDark)} value={editingContract.notify_days_before} onChange={e => setEditingContract(s => ({
              ...s,
              notify_days_before: Number(e.target.value)
            }))} />
              </label>
              <label className="flex items-center gap-2 text-sm mt-1">
                <input type="checkbox" className="w-5 h-5 accent-blue-500" checked={!!editingContract.auto_renew} onChange={e => setEditingContract(s => ({
              ...s,
              auto_renew: e.target.checked
            }))} />
                <span>Auto-Renew</span>
              </label>
            </div>

            {/* Empfänger-Auswahl im Bearbeiten-Modal */}
            <div className={`pt-3 border-t ${"border-white/10"}`}>
              <div className={`font-medium mb-2 ${"text-blue-100/90"}`}>
                📧 Empfänger (für Erinnerungs-E-Mails)
              </div>
              <AssigneePicker isDark={true} users={users} assigneeSearch={assigneeSearch} setAssigneeSearch={setAssigneeSearch} filteredUsers={filteredUsers} assigneeTmp={assigneeTmp} toggleAssignee={toggleAssignee} />
            </div>
          </form>}
      </Modal>}

      {/* Empfänger-Modal (Legacy - kann später entfernt werden) */}
      <Modal title="Empfänger wählen" open={assigneeModal.open} onClose={() => setAssigneeModal({
      open: false,
      contractId: null
    })} footer={<div className="flex justify-end gap-2">
            <AppButton type="button" variant="secondary" className={cn("px-3 py-1.5 text-sm min-h-[44px] font-normal", "")} onClick={() => setAssigneeModal({
        open: false,
        contractId: null
      })}>
              Abbrechen
            </AppButton>
            <AppButton type="button" variant="success" onClick={saveAssignees} disabled={saving} className={cn("px-3 py-1.5 text-sm min-h-[44px] font-normal inline-flex items-center gap-2", saving && successButtonLoadingClassName)}>
              Speichern
            </AppButton>
          </div>}>
        <AssigneePicker users={users} assigneeSearch={assigneeSearch} setAssigneeSearch={setAssigneeSearch} filteredUsers={filteredUsers} assigneeTmp={assigneeTmp} toggleAssignee={toggleAssignee} />
      </Modal>
    </>
  );
}
function AssigneePicker({
  isDark,
  users,
  assigneeSearch,
  setAssigneeSearch,
  filteredUsers,
  assigneeTmp,
  toggleAssignee
}) {
  const inputCls = "flex-1 bg-[#0b1324] text-blue-100 border border-white/10 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400/40";
  const listCls = `grid grid-cols-1 md:grid-cols-2 gap-2 overflow-y-auto pr-1 rounded-lg max-h-[50vh] md:max-h-[320px] custom-scrollbar ${"border border-white/10 bg-white/[0.02]"}`;
  const itemCls = "flex items-center gap-2 p-2 rounded bg-white/5 hover:bg-white/10 cursor-pointer";
  return <div className="mt-1">
      <div className="mb-2 flex items-center gap-2">
        <Search className={`w-4 h-4 ${"text-blue-200/60"}`} />
        <Input value={assigneeSearch} onChange={e => setAssigneeSearch(e.target.value)} placeholder="Mitarbeiter suchen…" className={inputCls} />
      </div>

      <div className={listCls} style={{
      scrollbarWidth: "thin"
    }}>
        {filteredUsers.length === 0 && <div className={`text-sm p-2 ${"opacity-70"}`}>Keine Benutzer gefunden.</div>}

        {filteredUsers.map(u => {
        const checked = assigneeTmp.includes(u.id);
        return <label key={u.id} className={itemCls}>
              <input type="checkbox" checked={checked} onChange={() => toggleAssignee(u.id)} className="accent-blue-500 shrink-0" />
              <span className="text-sm truncate">{u.name}</span>
            </label>;
      })}
      </div>
    </div>;
}